/**
 * The executive's own review request: his question, the admin's answer (or "done" note), and Got it, which
 * deletes both. Shown on the fill page (draft and finished) and under the template row on the project page.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorBox } from '../components/ui';
import { deleteReview, myReviewsKey, type Review } from './api';
import { ReviewTag } from './ReviewTag';

/** FR-5.8: documents stay on their version, so a changed template helps only in a new document. */
export function versionLine(r: Review, finished = false): string | null {
  if (r.state === 'open' || r.currentVersion === null || r.currentVersion <= r.templateVersion) return null;
  return `The admin updated the template to v${r.currentVersion}. Start a new document to use it; this ${finished ? 'document' : 'draft'} stays on v${r.templateVersion}.`;
}

/** `finished`: shown on a finished document (FinishedView), which is no longer a draft. */
export function MyReview({ review, finished = false }: { review: Review; finished?: boolean }) {
  const qc = useQueryClient();
  const gotIt = useMutation({
    mutationFn: () => deleteReview(review.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: myReviewsKey(review.projectId) }); },
  });
  const line = versionLine(review, finished);
  return (
    <section aria-label="Your review request" data-testid="my-review"
      className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">Your question to the admin</h2>
        <ReviewTag state={review.state} />
      </div>
      <p className="whitespace-pre-wrap text-slate-700">{review.message}</p>
      {review.state === 'open' && <p className="text-xs text-slate-500">Waiting for the admin's answer. Keep filling meanwhile.</p>}
      {review.state === 'answered' && review.reply !== null && (
        <div className="rounded bg-blue-50 p-2">
          <p className="text-xs font-medium text-blue-900">Admin replied{review.repliedBy ? ` (${review.repliedBy.displayName})` : ''}:</p>
          <p className="whitespace-pre-wrap text-blue-950">{review.reply}</p>
        </div>
      )}
      {review.state === 'done' && (
        <div className="rounded bg-green-50 p-2">
          {!line && <p className="text-green-900">The admin marked this done.</p>}
          {review.reply !== null && <p className="whitespace-pre-wrap text-green-950">{review.reply}</p>}
        </div>
      )}
      {line && <p className="font-medium text-amber-900">{line}</p>}
      <ErrorBox error={gotIt.error} />
      <div>
        <Button onClick={() => gotIt.mutate()} disabled={gotIt.isPending}
          title="Removes your question and the answer">Got it</Button>
      </div>
    </section>
  );
}
